import { useEffect, useMemo, useRef, useState } from "react";

import { MessageBubble } from "./MessageBubble";

const BUFFER_TOKENS = 4; // ~2 words held behind the latest, so the reveal always has runway
const DRAIN_AFTER_MS = 280; // no new tokens for this long ⇒ reveal the held-back tail
// Fixed cruise speed: the reveal runs at this rate regardless of how fast the
// model streams, so the animation pace stays consistent. Throughput only sets
// the floor — when the model is slower than cruise, the reveal is gated by what
// has arrived (it can't run ahead of the text). Tokens include whitespace, so
// this is ≈2× the visible word rate (~30 words/s).
const REVEAL_TPS = 60;
const EMIT_INTERVAL_MS = 33; // cap markdown re-parse to ~30fps

/**
 * Reveal the streamed text word-by-word at a fixed cruise speed, decoupled from
 * how bursty the deltas arrive. Holds a small buffer behind the latest token so
 * the animation flows smoothly; returns a growing prefix string rendered as live
 * markdown for the per-word fade at the frontier.
 */
function useSmoothReveal(target: string): string {
  const tokens = useMemo(() => target.split(/(\s+)/), [target]);
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;
  const countRef = useRef(0);
  const [shown, setShown] = useState("");

  // Target got shorter (new block / reset) — re-reveal from the start.
  if (tokens.length < countRef.current) countRef.current = 0;

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let prevTotal = tokensRef.current.length;
    let lastGrowth = last;
    let lastEmit = 0;
    const loop = (now: number) => {
      const dt = now - last;
      last = now;
      const total = tokensRef.current.length;
      if (total !== prevTotal) { prevTotal = total; lastGrowth = now; }
      const draining = now - lastGrowth > DRAIN_AFTER_MS;
      const limit = draining ? total : Math.max(0, total - BUFFER_TOKENS);
      if (countRef.current < limit) {
        acc += (REVEAL_TPS * dt) / 1000;
        if (acc >= 1) {
          countRef.current = Math.min(limit, countRef.current + Math.floor(acc));
          acc -= Math.floor(acc);
          // Always emit on reaching the ceiling so the frontier never lags.
          if (now - lastEmit >= EMIT_INTERVAL_MS || countRef.current >= limit) {
            lastEmit = now;
            setShown(tokensRef.current.slice(0, countRef.current).join(""));
          }
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return shown;
}

/** Transient assistant bubble for the live token stream. ``created_at: ""``
 *  suppresses the action row; ``streaming`` renders live markdown with the
 *  per-word fade. */
export function StreamingBubble({ text }: { text: string }) {
  const shown = useSmoothReveal(text);
  return (
    <MessageBubble
      message={{
        id: "__streaming__",
        role: "assistant",
        content_json: JSON.stringify([{ type: "text", text: shown }]),
        created_at: "",
      }}
      streaming
    />
  );
}
